import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Sparkle } from "@phosphor-icons/react";
import ShareButton from "@/components/ShareButton";
import SmartStoryLink from "@/components/SmartStoryLink";
import { http } from "@/lib/api";
import ListenTranslateBar from "@/components/ListenTranslateBar";

/**
 * Articles — index of long-form NaturePulse essays and reports.
 *
 * Each article is defined declaratively so new pieces can be added by
 * adding a row here (or, later, loaded from a CMS collection).
 */
const IMG_WATERFALL = "https://customer-assets-m6fa6gv7.emergentagent.net/job_green-timeline/artifacts/kirsyd6c_naturepulse-1.webp";
const IMG_LABURNUM = "https://customer-assets-m6fa6gv7.emergentagent.net/job_green-timeline/artifacts/tp1rr943_naturepulse6.jpeg";

const ARTICLES = [
  {
    slug: "living-record",
    title: "Building a Living, Real-Time Record of Our Changing Planet",
    subtitle: "What if humanity could document nature every day, everywhere?",
    kicker: "Essay · The observation network",
    cover: IMG_LABURNUM,
    excerpt:
      "Satellites see the planet from above. Weather stations measure the atmosphere. But no one is continuously recording what is happening to nature in every community on Earth. NaturePulse could fill that gap — one GPS-tagged observation at a time.",
    to: "/article/living-record",
    reading: "18 min read",
    published: "Aug 2026",
    hashtags: ["NaturePulse", "CitizenScience", "ClimateAction"],
  },
  {
    slug: "living-diary",
    title: "Documenting the Living Planet, One Day at a Time",
    subtitle: "The NaturePulse Mission",
    kicker: "Mission · Manifesto",
    cover: IMG_WATERFALL,
    excerpt:
      "What if we started documenting our planet every day? A photograph. A location. A date. A few words. Together, these small observations become a continuously growing record of the living world.",
    to: "/manifesto",
    reading: "12 min read",
    published: "Aug 2026",
    hashtags: ["NaturePulse", "Nature", "ClimateAction"],
  },
  // Future article rows go here — the layout scales.
];

// Placeholder card shown at the end so contributors know more essays are welcome.
function ContributorSlot() {
  return (
    <div className="rounded-2xl border border-dashed border-black/25 bg-white/40 p-6 flex flex-col items-start" data-testid="articles-contribute">
      <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
        / Contribute
      </div>
      <div className="font-serif-display text-2xl leading-tight">Write for NaturePulse.</div>
      <p className="mt-2 text-sm text-[#3A3830]">
        Field notes, seasonal observations, restoration stories, community projects. If you have something to share, we'd love to publish it.
      </p>
      <Link
        to="/contact"
        data-testid="articles-contribute-link"
        className="mt-auto pt-4 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:underline"
      >
        Pitch a piece <ArrowRight size={12} weight="bold" />
      </Link>
    </div>
  );
}

function ArticleCard({ a, featured }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`group relative rounded-2xl overflow-hidden bg-white/70 border border-black/10 hover:border-[color:var(--np-brand,#B85F2A)] transition-colors flex flex-col ${featured ? "md:col-span-2" : ""}`}
      data-testid={`article-${a.slug}`}
    >
      <Link to={a.to} className={`flex-1 flex ${featured ? "md:flex-row" : "flex-col"}`}>
        {a.cover && (
          <div className={`overflow-hidden bg-black/5 ${featured ? "md:w-1/2 md:aspect-auto aspect-[4/3]" : "aspect-[16/10]"}`}>
            <img src={a.cover} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
          </div>
        )}
        <div className={`p-6 md:p-8 flex-1 flex flex-col ${featured ? "md:justify-center" : ""}`}>
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            / {a.kicker}
          </div>
          <h2 className={`mt-2 font-serif-display leading-[1.05] tracking-tight ${featured ? "text-3xl md:text-4xl" : "text-2xl"}`}>
            {a.title}
          </h2>
          {a.subtitle && <div className="mt-1 font-serif-display italic text-[#3A3830]">{a.subtitle}</div>}
          <p className="mt-3 text-[15px] text-[#241209] leading-relaxed line-clamp-4">{a.excerpt}</p>
          <div className="mt-4 flex items-center gap-3 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)]">
            <span>{a.published}</span>
            <span>·</span>
            <span>{a.reading}</span>
          </div>
        </div>
      </Link>
      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <ShareButton
          url={`${window.location.origin}${a.to}`}
          title={a.title}
          text={a.excerpt.slice(0, 140)}
          hashtags={a.hashtags}
          variant="icon"
          testid={`article-share-${a.slug}`}
        />
      </div>
    </motion.article>
  );
}

export default function Articles() {
  const [featured, ...rest] = ARTICLES;
  const [stories, setStories] = useState([]);
  const [guides, setGuides] = useState([]);

  useEffect(() => {
    http.get("/cms/pages", { params: { page_type: "articles", tag: "nature-pulse", limit: 12 } })
      .then(({ data }) => setGuides(data.pages || [])).catch(() => {});
  }, []);

  // Blend the hand-crafted essays with long-form AI content already published
  // in the archive: flagship editorials, composed news_link editorials,
  // research summaries and nature memories all belong on /articles
  // alongside the mission essays.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const params = { limit: 12 };
        // Backend supports one story_type at a time — call in parallel
        // and dedupe. Fast, and each request is <30ms.
        const [flagship, a, b, c] = await Promise.all([
          http.get("/nature/stories/list/all", { params: { ...params, story_type: "article" } }),
          http.get("/nature/stories/list/all", { params: { ...params, story_type: "news_link" } }),
          http.get("/nature/stories/list/all", { params: { ...params, story_type: "research" } }),
          http.get("/nature/stories/list/all", { params: { ...params, story_type: "nature_memory" } }),
        ]);
        if (!alive) return;
        // Flagship editorials rank first, then the archive by recency.
        const flag = (flagship.data.stories || []).filter((s) => (s.body || "").length > 200);
        const rest = [...(a.data.stories || []), ...(b.data.stories || []), ...(c.data.stories || [])]
          .filter((s) => (s.body || "").length > 200)
          .sort((x, y) => (y.event_date || y.publication_date || y.created_at || "").localeCompare(x.event_date || x.publication_date || x.created_at || ""));
        // Dedupe by id in case an article overlaps multiple queries.
        const seen = new Set();
        const merged = [...flag, ...rest].filter((s) => {
          if (seen.has(s.id)) return false;
          seen.add(s.id); return true;
        }).slice(0, 15);
        setStories(merged);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <main className="bg-[color:var(--np-bg,#F5EBDC)] text-[color:var(--np-text,#1B4332)] min-h-screen pt-32 md:pt-36 pb-32" data-testid="articles-page">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10">
        <ListenTranslateBar text="Write for NaturePulse — contribute articles and field notes to the archive." testidPrefix="ltbar-articles" />
      </div>
      <section className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2 flex items-center gap-2">
          <BookOpen size={12} weight="fill" /> / Articles
        </div>
        <h1 className="font-serif-display text-4xl md:text-6xl leading-[0.95] tracking-tighter">
          Long reads from NaturePulse.
        </h1>
        <p className="mt-3 font-serif-display text-base md:text-lg text-[#3A3830] max-w-2xl">
          Mission essays, field notes and community reports. Each piece is translatable and read-aloud-ready.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
        {featured && <ArticleCard a={featured} featured />}
        {rest.map((a) => <ArticleCard key={a.slug} a={a} />)}
        <ContributorSlot />
      </section>

      {guides.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-16" data-testid="articles-desk-guides">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#1B4332] flex items-center gap-2 mb-4">
            <BookOpen size={12} weight="fill" /> / Field guides &amp; explainers from the desk
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {guides.map((g) => (
              <Link key={g.slug} to={`/read/articles/${g.slug}`} data-testid={`articles-guide-${g.slug}`}
                className="group rounded-2xl bg-white/70 border border-black/10 p-5 hover:border-[#1B4332] hover:-translate-y-0.5 transition-all flex flex-col">
                <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#1B4332]/70">{g.category || "Guide"}</div>
                <h3 className="mt-2 font-serif-display text-xl leading-tight">{g.title}</h3>
                <p className="mt-2 text-sm text-[#3A3830] line-clamp-3">{g.excerpt || g.subtitle}</p>
                <div className="mt-auto pt-3 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[color:var(--np-brand,#B85F2A)] inline-flex items-center gap-1">
                  Read <ArrowRight size={11} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {stories.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-16" data-testid="articles-from-archive">
          <div className="flex items-baseline justify-between gap-3 mb-4">
            <div>
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
                <Sparkle size={12} weight="fill" /> / From the archive
              </div>
              <h2 className="mt-1 font-serif-display text-2xl md:text-3xl leading-tight">
                Editorial stories, research summaries & nature memories.
              </h2>
            </div>
            <Link to="/stories" className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:opacity-80">
              Full archive →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stories.map((s) => (
              <SmartStoryLink
                key={s.id}
                story={s}
                data-testid={`article-story-${s.id}`}
                className="group rounded-2xl overflow-hidden bg-white/70 border border-black/10 hover:border-[color:var(--np-brand,#B85F2A)] transition-colors flex flex-col"
              >
                {s.media?.[0]?.url ? (
                  <div className="aspect-[16/10] overflow-hidden bg-black/5">
                    <img src={s.media[0].url} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                  </div>
                ) : (
                  <div className="aspect-[16/8] bg-gradient-to-br from-[#FBF2E1] to-[#F5EBDC] border-b border-black/5 grid place-items-center">
                    <BookOpen size={26} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]/70" />
                  </div>
                )}
                <div className="p-5 flex-1 flex flex-col">
                  <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                    {s.story_type?.replace(/_/g, " ")} · {s.category}
                  </div>
                  <h3 className="mt-2 font-serif-display text-lg leading-tight line-clamp-3">
                    {s.title || s.headline}
                  </h3>
                  {s.summary && <p className="mt-2 text-sm text-[#3A3830] line-clamp-3">{s.summary}</p>}
                  <div className="mt-auto pt-3 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)]">
                    {s.publisher && <span>{s.publisher} · </span>}
                    {(s.event_date || s.publication_date) && new Date(s.event_date || s.publication_date).toISOString().slice(0, 10)}
                  </div>
                </div>
              </SmartStoryLink>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
